import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewRef } from '@angular/core';
import { combineLatest, Observable, of, Subscription, throwError } from 'rxjs';
import { map, mergeMap, skip, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { DsBannerState, DsColors, DsPageMessageVariants, Icons, IconScale, IconStyles } from '@levelaccess/design-system';

import { Api } from '@shared/constants/api';
import { IScanSummaries, IScanSummaryAllItem } from '@shared/interfaces/scan-summaries.interface';
import { errorMessagesNames } from '@shared/constants/errors';
import { IScan } from '@shared/interfaces/scan.interface';
import { $scan } from '@shared/constants/scan';
import { IScanTag } from '@shared/interfaces/scan-tag.interface';
import { IDigitalPropertyDetails } from '@shared/interfaces/digital-property.interface';
import { $digitalProperty } from '@shared/constants/digital-properties';
import {
  AccessibilityAuditToolNames,
  AuditToolNameToAuditType,
  ToolCommonNameToAuditToolName,
} from '@shared/constants/audit-tool';
import { standardNameAndLevel, wcagNameAndLevels } from '@shared/constants/scanning';
import { $scanSummaries } from '@shared/constants/scan-summaries';
import { IScanAudit } from '@shared/interfaces/scan-audit.interface';
import { $scanAudit } from '@shared/constants/scan-audit';
import { $scanTag, SystemTags } from '@shared/constants/scan-tag';
import { AuditTypes } from '@shared/constants/audit-types';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { ICustomSeveritiesMap } from '@shared/interfaces/tenant.interface';
import { FeatureFlagCollection, revertFlag } from '@shared/interfaces/feature-flag.interface';
import { IUserWayTenantIntegration } from '@shared/interfaces/userway-integration.interface';
import { RequiredSecurities } from '@shared/constants/required-securities';
import { IRequiredSecurity } from '@shared/interfaces/security.interface';
import { SharedAuditsUtility } from '@shared/utils/audits.utility';
import { $severity } from '@shared/constants/accessibility';

import { ScanSummariesService } from '@client/app/services/scan-summaries.service';
import { ICurrentSelectedProperty } from '@client/app/services/user.service';
import { UserPropertyService } from '@client/app/services/user-property.service';
import { BusMessageChannels, BusMessageService } from '@client/app/services/bus-message.service';
import { ScanService } from '@client/app/services/scan.service';
import { ScanTagService } from '@client/app/services/scan-tag.service';
import { DigitalPropertyService } from '@client/app/services/digital-property.service';
import { LoadErrorHandler } from '@client/app/services/load-error-handler';
import { pageLoadingState } from '@client/app/components/page-loading-state/page-loading-state.component';
import { TenantSeveritiesService } from '@client/app/services/tenant-severities.service';
import { TenantService } from '@client/app/services/tenant.service';
import { ChatContextRegistry } from '@client/app/services/chat-context-registry.service';

@Component({
  selector: 'app-scan-results',
  templateUrl: './scan-results.component.html',
  styleUrls: ['./scan-results.component.scss'],
  standalone: false,
})
export class ScanResultsComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;
  private scanSummaries: IScanSummaries;
  private previousScanSummaries: IScanSummaries;
  private digitalProperty: IDigitalPropertyDetails;

  public get digitalPropertyId(): string {
    return this.digitalProperty?.[$digitalProperty._id] || null;
  }

  public get canViewByComponent(): boolean {
    return (
      !this.digitalProperty[$digitalProperty.componentMatcherOnlyDefaultTool] ||
      (this.auditTool &&
        this.digitalProperty &&
        ToolCommonNameToAuditToolName[this.digitalProperty[$digitalProperty.defaultTool]] === this.auditTool)
    );
  }

  public workspaceId: string;
  public AccessibilityAuditToolNames: typeof AccessibilityAuditToolNames;
  public scan: IScan;
  public scanSummary: IScanSummaryAllItem;
  public previousScanSummary: IScanSummaryAllItem;
  public scanId: string;
  public scanAudit: IScanAudit;
  public auditTool: AccessibilityAuditToolNames;
  public standardAndLevel: standardNameAndLevel;
  public $scanSummaries: typeof $scanSummaries;
  public pageLoadingState: typeof pageLoadingState;
  public loadingState: pageLoadingState;
  public toolsUsed: string[];
  public isMonitoringScan: boolean;
  public dpDefaultTool: AccessibilityAuditToolNames;
  public customSeverities$: Observable<ICustomSeveritiesMap>;
  public FeatureFlags: typeof FeatureFlagCollection;
  public revertFlag: typeof revertFlag = revertFlag;
  public artScansAndMonitoringReadRequiredSecurity: IRequiredSecurity;
  public digitalPropertyHasUserWayIntegration$: Observable<boolean>;
  public tenantHasUserWayIntegration$: Observable<boolean>;
  public userWayBannerLink$: Observable<string>;
  public userWayBannerLinkMessage$: Observable<string>;
  public Icons: typeof Icons = Icons;
  public IconStyles: typeof IconStyles = IconStyles;
  public IconScale: typeof IconScale = IconScale;
  public DsColors: typeof DsColors = DsColors;
  public DsPageMessageVariants: typeof DsPageMessageVariants = DsPageMessageVariants;

  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;

  public constructor(
    private activatedRoute: ActivatedRoute,
    private userPropertyService: UserPropertyService,
    private scanSummariesService: ScanSummariesService,
    private router: Router,
    private busMessageService: BusMessageService,
    private scanService: ScanService,
    private scanTagService: ScanTagService,
    private loadErrorHandler: LoadErrorHandler,
    private digitalPropertyService: DigitalPropertyService,
    private tenantSeveritiesService: TenantSeveritiesService,
    private tenantService: TenantService,
    private changeDetectorRef: ChangeDetectorRef,
    private chatContextRegistry: ChatContextRegistry,
  ) {
    this.subscriptions = new Subscription();
    this.AccessibilityAuditToolNames = AccessibilityAuditToolNames;
    this.$scanSummaries = $scanSummaries;
    this.pageLoadingState = pageLoadingState;
    this.loadingState = pageLoadingState.loading;
    this.FeatureFlags = FeatureFlagCollection;
    this.artScansAndMonitoringReadRequiredSecurity = RequiredSecurities.ART_Scans_And_Monitoring_Read;

    this.scanId = null;
    this.workspaceId = null;
    this.scanSummary = null;
    this.previousScanSummary = null;
    this.standardAndLevel = null;
    this.toolsUsed = [];
    this.digitalProperty = null;
    this.isMonitoringScan = false;
  }

  private getScan(): Observable<IScan> {
    if (typeof this.scanId === 'undefined') {
      console.warn('[ScanResultsComponent.getScan] scan ID is not available');
      throwError(null);
    }

    return this.scanService.getScan(this.scanId);
  }

  private async onGetScanError(error: HttpErrorResponse): Promise<void> {
    if (error.error.app.name === errorMessagesNames.ScanDoesntBelongToRequestedDigitalProperty) {
      await this.router.navigate(['/', Api.scans]);
    } else {
      this.loadErrorHandler.handleError('error_retrieving_scan', error, ScanResultsComponent.name);
    }
    this.loadingState = pageLoadingState.error;
    this.detectChanges();
  }

  private onGetScanSuccess(scan: IScan): void {
    this.subscriptions.add(
      this.loadScanTag(scan)
        .pipe(
          tap(() => {
            this.scan = scan;
            this.isMonitoringScan = scan[$scan.tag][$scanTag.tagName] === SystemTags.Monitoring;

            // mobile scans don't rely on digital property's default tool
            const mobileTool: AccessibilityAuditToolNames | null = SharedAuditsUtility.getMobileAccessibilityAuditToolName(
              scan[$scan.scanOrigin],
            );
            if (SharedCommonUtility.notNullish(mobileTool)) {
              this.auditTool = mobileTool;
            } else if (SharedCommonUtility.isNullish(this.auditTool)) {
              this.auditTool = this.dpDefaultTool;
            }
          }),
          switchMap(() =>
            combineLatest([this.getPreviousMonitoringScanSummary(), this.getScanSummaries()]).pipe(
              map(([previousScanSummaries, scanSummaries]: [IScanSummaries, IScanSummaries]): IScanSummaries => {
                this.previousScanSummaries = previousScanSummaries;
                return scanSummaries;
              }),
            ),
          ),
        )
        .subscribe({
          next: this.onGetScanSummarySuccess.bind(this),
          error: this.onGetScanSummaryError.bind(this),
        }),
    );
  }

  private getMonitoringToolFromSummary(summaries: IScanSummaries): AccessibilityAuditToolNames {
    if (summaries?.[this.dpDefaultTool]) {
      return this.dpDefaultTool;
    }

    for (const tool in summaries) {
      if (summaries[tool]) {
        return tool as AccessibilityAuditToolNames;
      }
    }

    return undefined;
  }

  private async onGetScanSummarySuccess(scanSummaries: IScanSummaries): Promise<void> {
    this.loadingState = pageLoadingState.loaded;
    this.scanSummaries = scanSummaries;
    this.detectChanges();

    if (this.isMonitoringScan) {
      this.auditTool = this.getMonitoringToolFromSummary(scanSummaries);
      this.previousScanSummary = this.previousScanSummaries?.[this.getMonitoringToolFromSummary(this.previousScanSummaries)];
    }

    this.scanSummary = scanSummaries[this.auditTool];

    for (const tool of Object.keys(this.scanSummaries)) {
      if (!SharedCommonUtility.isNullish(this.scanSummaries[tool][$scanSummaries.metrics])) {
        this.toolsUsed.push(tool);
      }
    }
    this.registerScanResultsChatContext();

    if (!SharedCommonUtility.isNullishOrEmpty(this.toolsUsed) && !this.toolsUsed.includes(this.auditTool)) {
      this.auditTool = this.toolsUsed[0] as AccessibilityAuditToolNames;
      this.onAuditToolChange(this.auditTool);
    }

    if (SharedCommonUtility.isNullish(this.scanSummary)) {
      return;
    }

    this.scanAudit = this.scanSummary[$scanSummaries.scanAuditsId] as IScanAudit;
    const auditType: AuditTypes = AuditToolNameToAuditType[this.auditTool];
    this.standardAndLevel = this.scanAudit[$scanAudit.audits][auditType].includeStandard;
  }

  private onGetScanSummaryError(error: HttpErrorResponse): void {
    this.loadingState = pageLoadingState.error;
    this.loadErrorHandler.handleError('error_getting_scan_summary_for_tool', error, ScanResultsComponent.name);
    this.detectChanges();
  }

  private detectChanges(): void {
    if ((this.changeDetectorRef as ViewRef).destroyed) {
      return;
    }
    this.changeDetectorRef.markForCheck();
  }

  private loadScanTag(scan: IScan): Observable<IScan> {
    return this.scanTagService.getScanTag(this.workspaceId, this.digitalPropertyId, scan[$scan.tag] as string).pipe(
      map((tag: IScanTag): IScan => {
        scan[$scan.tag] = tag;
        return scan;
      }),
    );
  }

  private getPreviousMonitoringScanSummary(): Observable<IScanSummaries> {
    return this.activatedRoute.data.pipe(
      switchMap(({ failOnNonMonitoringTag }: Data) => {
        if (this.isMonitoringScan) {
          return this.scanSummariesService.getPreviousSummariesForScan(this.workspaceId, this.digitalPropertyId, this.scanId);
        }
        if (failOnNonMonitoringTag) {
          this.router.navigate(['404']);
        }
        return of(null);
      }),
    );
  }

  private getScanSummaries(): Observable<IScanSummaries> {
    return this.scanSummariesService.getSummariesForScan(this.workspaceId, this.digitalPropertyId, this.scanId);
  }

  public onAuditToolChange(auditTool: AccessibilityAuditToolNames): void {
    this.auditTool = auditTool;
    this.scanSummary = this.scanSummaries[this.auditTool];
    this.scanAudit = this.scanSummary[$scanSummaries.scanAuditsId] as IScanAudit;

    const auditType: AuditTypes = AuditToolNameToAuditType[this.auditTool];
    this.standardAndLevel = this.scanAudit[$scanAudit.audits][auditType].includeStandard;

    if (!SharedCommonUtility.isNullish(this.previousScanSummaries)) {
      this.previousScanSummary = this.previousScanSummaries[this.auditTool];
    }

    this.router
      .navigate(['.'], {
        relativeTo: this.activatedRoute,
        queryParams: { toolName: auditTool },
      })
      .then(() => this.registerScanResultsChatContext());
  }

  public get canARTImproveScore(): boolean {
    const score: number = Math.floor(this.scanSummary[$scanSummaries.score]);
    const simulatedARTScore: number = Math.floor(this.scanSummary[$scanSummaries.simulatedARTScore]);
    return simulatedARTScore > score;
  }

  public get simulatedARTScore(): number {
    return Math.floor(this.scanSummary[$scanSummaries.simulatedARTScore]);
  }

  public get totalFindingsCountAfterART(): number {
    return (
      SharedAuditsUtility.getTotalIssuesCountExcluding(
        this.auditTool,
        this.scanSummary?.[$scanSummaries.scanAuditsId] as IScanAudit,
        this.scanSummary?.[$scanSummaries.metrics],
        new Set([$severity.info]),
      ) - this.scanSummary[$scanSummaries.failedARTFindingsCount]
    );
  }

  public get isWCAGScan(): boolean {
    return wcagNameAndLevels.includes(this.standardAndLevel);
  }

  public pageDataLoaded(): boolean {
    return this.loadingState === pageLoadingState.loaded;
  }

  private registerScanResultsChatContext(): void {
    this.chatContextRegistry.setContext('scanResults', { scanId: this.scanId });
  }

  public async ngOnInit(): Promise<void> {
    this.scanId = this.activatedRoute.snapshot.paramMap.get(Api.scanId);
    this.registerScanResultsChatContext();

    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe((params: Record<string, string>) => {
        const toolName: AccessibilityAuditToolNames = params.toolName as AccessibilityAuditToolNames;
        if (Object.values(AccessibilityAuditToolNames).includes(toolName)) {
          this.auditTool = toolName;
        }
      }),
    );

    const digitalPropertyDetails$: Observable<IDigitalPropertyDetails> = this.userPropertyService.currentSelectedProperty().pipe(
      mergeMap(({ digitalPropertyId, workspaceId }: ICurrentSelectedProperty): Observable<IDigitalPropertyDetails> => {
        return this.digitalPropertyService.getDigitalProperty(workspaceId, digitalPropertyId);
      }),
    );
    this.subscriptions.add(
      digitalPropertyDetails$
        .pipe(
          tap((selectedProperty: IDigitalPropertyDetails): void => {
            this.digitalProperty = selectedProperty;
            this.workspaceId = selectedProperty[$digitalProperty.workspace]._id;
            this.dpDefaultTool = ToolCommonNameToAuditToolName[selectedProperty[$digitalProperty.defaultTool]];
          }),
          switchMap(this.getScan.bind(this)),
        )
        .subscribe({
          next: this.onGetScanSuccess.bind(this),
          error: this.onGetScanError.bind(this),
        }),
    );

    this.subscriptions.add(
      this.busMessageService
        .from(BusMessageChannels.digitalPropertyChange)
        .pipe(skip(1))
        .subscribe(() => {
          if (this.scanSummary !== null) {
            this.router.navigate(['/', Api.scans]);
          }
        }),
    );

    this.customSeverities$ = this.tenantSeveritiesService.getAll();

    this.digitalPropertyHasUserWayIntegration$ = digitalPropertyDetails$.pipe(
      map((digitalPropertyDetails: IDigitalPropertyDetails) => {
        return SharedCommonUtility.notNullishOrEmpty(digitalPropertyDetails[$digitalProperty.userWaySiteHostName]);
      }),
    );

    this.tenantHasUserWayIntegration$ = this.tenantService.getUserWayAccess().pipe(
      map((userWayTenantIntegration: IUserWayTenantIntegration) => {
        return userWayTenantIntegration.enabled;
      }),
    );

    this.userWayBannerLink$ = combineLatest([this.tenantHasUserWayIntegration$, this.digitalPropertyHasUserWayIntegration$]).pipe(
      map(([tenantHasUserWayIntegration, digitalPropertyHasUserWayIntegration]: [boolean, boolean]) => {
        if (tenantHasUserWayIntegration && digitalPropertyHasUserWayIntegration) {
          return 'https://client.levelaccess.com/hc/en-us/articles/31059553835799';
        } else if (tenantHasUserWayIntegration && !digitalPropertyHasUserWayIntegration) {
          return 'https://client.levelaccess.com/hc/en-us/articles/25921127375895';
        }
        return 'https://client.levelaccess.com/hc/en-us/articles/31059428597143';
      }),
    );

    this.userWayBannerLinkMessage$ = combineLatest([
      this.tenantHasUserWayIntegration$,
      this.digitalPropertyHasUserWayIntegration$,
    ]).pipe(
      map(([tenantHasUserWayIntegration, digitalPropertyHasUserWayIntegration]: [boolean, boolean]) => {
        if (tenantHasUserWayIntegration && digitalPropertyHasUserWayIntegration) {
          return 'how_widget_affects_scan_results';
        } else if (tenantHasUserWayIntegration && !digitalPropertyHasUserWayIntegration) {
          return 'how_to_set_up_widget';
        }
        return 'learn_about_widget';
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
